import { ANALYTICS_TIMESTAMP_FORMAT } from '@app/core/analytics.service';
import { CareTopic } from '@app/shared/care-topic';
import { formatDate } from '@app/shared/date-format.pipe';
import { ServiceArea } from '@app/shared/service-area';
import { State } from '@app/shared/state.service';

import { AppointmentBookingState } from '../appointment-booking-state-service/appointment-booking-state';
import { AppointmentRoutingState } from '../appointment-routing-state-service/appointment-routing-state';
import { AppointmentSearchMetaData } from '../appointment-search.service';
import { AppointmentType } from '../appointment-type';
import { InventoryFilterOffice } from '../search-results/appointment-filter-container/appointment-location-filter/appointment-location-filter.component';

interface TrackableSearchState {
  remoteAppointmentTypeId?: number;
  onsite_only: boolean;
  is_widget_shown: boolean;
  selected_date: string;
  selected_offices?: string[];
  provider_count?: number;
  inventory_count?: number;
  inventory_ids?: number[];
  widget_provider_count?: number;
  widget_inventory_count?: number;
  widget_inventory_ids?: number[];
  office_ids?: number[];
}

export interface AppointmentSearchParams {
  service_area_id: number;
  state?: string;
  office_ids: number[];
  appointment_type_id: number;
  onsite_only: boolean;
  date_start: string;
  date_end: string;
  recommend_remote_results?: boolean;
  provider_id?: string;
}

export class AppointmentSearchState {
  searchMetadata: AppointmentSearchMetaData;
  dateStart: Date;
  dateEnd: Date;
  onsiteOnly = false;
  selectedOffices: InventoryFilterOffice[] = [];
  selectedProviderId?: string;

  private bookingState: AppointmentBookingState;
  private routingState?: AppointmentRoutingState;

  constructor(bookingState: AppointmentBookingState, routingState?: AppointmentRoutingState) {
    this.bookingState = bookingState;
    this.routingState = routingState;
  }

  get officeIds(): number[] {
    return this.selectedOffices.length > 0 ? this.selectedOffices.map(o => o.id) : [];
  }

  getBookingSource(): string {
    return this.bookingState?.source;
  }

  get searchParams(): AppointmentSearchParams {
    const appointmentType: AppointmentType = this.bookingState.appointmentType;
    const params: AppointmentSearchParams = {
      service_area_id: this.serviceArea.id,
      state: this.state?.shortName,
      office_ids: this.officeIds,
      appointment_type_id: appointmentType.id,
      onsite_only: this.onsiteOnly,
      date_start: formatDate(this.dateStart, 'yyyy-MM-dd'),
      date_end: formatDate(this.dateEnd, 'yyyy-MM-dd'),
      recommend_remote_results: this.recommend_remote_results,
    };

    if (this.selectedProviderId) {
      params.provider_id = this.selectedProviderId;
    }

    return params;
  }

  get trackableProperties(): TrackableSearchState {
    const properties: TrackableSearchState = {
      onsite_only: this.onsiteOnly,
      selected_date: formatDate(this.dateStart, ANALYTICS_TIMESTAMP_FORMAT),
      is_widget_shown: false,
    };

    const metadata: AppointmentSearchMetaData = this.searchMetadata;
    if (!!metadata) {
      properties.inventory_count = metadata.inventoryCount;
      properties.inventory_ids = metadata.inventoryIds;
      properties.provider_count = metadata.providerCount;
      properties.remoteAppointmentTypeId = metadata.remoteAppointmentTypeId;
      properties.is_widget_shown = metadata.recommendedRemoteResultsProviderCount > 0;
      properties.widget_provider_count = metadata.recommendedRemoteResultsProviderCount;
      properties.widget_inventory_count = metadata.recommendedRemoteResultsInventoryCount;
      properties.widget_inventory_ids = metadata.recommendedRemoteResultsInventoryIds;
    }

    if (!!this.selectedOffices && this.selectedOffices.length > 0) {
      properties.selected_offices = this.selectedOffices.map(o => o.name);
    }

    return properties;
  }

  get serviceArea(): ServiceArea {
    return this.bookingState.getSelectedServiceArea();
  }

  get state(): State | undefined {
    if (
      this.bookingState.hasAccessToRemotePrimaryCare() &&
      (this.routingState?.topicName === CareTopic.RemoteVisit || this.bookingState.isRescheduling())
    ) {
      return this.bookingState.getSelectedState();
    }
    return undefined; // returning undefined by default, so it remains excluded from params
  }

  get appointmentType(): AppointmentType {
    return this.bookingState.appointmentType;
  }

  get appointmentBookingState(): AppointmentBookingState {
    return this.bookingState;
  }

  // recommending remote results, except for office visits to remote primary care eligible employees
  get recommend_remote_results(): boolean {
    if (this.routingState?.topicName === CareTopic.OfficeVisit && this.bookingState.hasAccessToRemotePrimaryCare()) {
      return false;
    }
    return true;
  }
}
